import { useState, useCallback } from 'react'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer'
import {
  cleanAudioNoteSummary,
  formatTranscriptForCopy,
  mapTranscriptEntriesToMessages,
} from '@renderer/utils/audio-note-helpers'
import { TranscriptEntry } from '@highlight/common'

/**
 * Hook for copying audio note summary to clipboard
 */
export function useCopySummary(summary: string | undefined) {
  const [copied, setCopied] = useState(false)

  const cleanedSummary = cleanAudioNoteSummary(summary)

  const handleCopy = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation()
      }

      if (!cleanedSummary) {
        return
      }

      try {
        const html = DOMPurify.sanitize(marked.parse(cleanedSummary, { async: false, gfm: true }))
        await OverlayIpcRenderer.writeTextToClipboard(cleanedSummary, html)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy summary:', err)
      }
    },
    [cleanedSummary],
  )

  return {
    copied,
    handleCopy,
    hasSummary: !!cleanedSummary,
  }
}

/**
 * Hook for copying audio note transcript to clipboard
 */
export function useCopyTranscript(transcriptEntries: TranscriptEntry[] | undefined) {
  const [copied, setCopied] = useState(false)

  const transcriptMessages = transcriptEntries ? mapTranscriptEntriesToMessages(transcriptEntries) : []
  const formattedTranscript = formatTranscriptForCopy(transcriptMessages)

  const handleCopy = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation()
      }

      if (!formattedTranscript) return

      try {
        await OverlayIpcRenderer.writeTextToClipboard(formattedTranscript)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy transcript:', err)
      }
    },
    [formattedTranscript],
  )

  return {
    copied,
    handleCopy,
    hasTranscript: !!transcriptEntries && transcriptEntries.length > 0,
  }
}
